import type { PartnerPackagingV2 } from '../../store/services/PartnerService/types';

/**
 * Sorters for the Configured Packaging table (see PackagingTableV2).
 *
 * Extracted from the table's inline column config so the comparators can be
 * unit-tested and reused. See CUS-16855: the previous inline comparators used
 * raw `<`/`>` relational operators, which compare strings by UTF-16 code unit
 * and therefore sort every lowercase-initial value AFTER all uppercase-initial
 * values. `stringSorter` uses `localeCompare` with case-insensitive, numeric
 * collation instead.
 */

type StringAccessor = (row: PartnerPackagingV2) => string | undefined;

/**
 * Case-insensitive, number-aware comparator for a string field.
 * Empty/missing values sort last (preserving the table's prior empty-value guards).
 */
export const stringSorter =
  (getValue: StringAccessor) =>
  (a: PartnerPackagingV2, b: PartnerPackagingV2): number => {
    const aVal = getValue(a);
    const bVal = getValue(b);
    if (!aVal && bVal) return 1;
    if (!bVal && aVal) return -1;
    if (!aVal || !bVal) return 0;
    return aVal.localeCompare(bVal, undefined, { sensitivity: 'base', numeric: true });
  };

export const nameSorter = stringSorter((row) => row.name);
export const descriptionSorter = stringSorter((row) => row.description);
export const packagingSizeNameSorter = stringSorter((row) => row.packagingSizeName);
export const packagingIdSorter = stringSorter((row) => row.packagingId);
export const partnerProvidedIdSorter = stringSorter((row) => row.partnerProvidedId);
export const packagingMaterialSorter = stringSorter((row) => row.packagingMaterial);

/**
 * Sorts by TOTAL linear dimension (length + width + height). This is the
 * intended behavior — the column shows "L x W x H", so ordering by the total
 * size is the meaningful key. Kept explicit here to prevent a future "fix" that
 * sorts by a single dimension.
 *
 * Each dimension is optional in the generated spec, so a missing one counts as 0
 * rather than turning the whole total into NaN (which would compare false against
 * everything and scramble the column).
 */
const totalLinearDimension = ({ linearDimensions }: PartnerPackagingV2): number =>
  (linearDimensions.length ?? 0) + (linearDimensions.width ?? 0) + (linearDimensions.height ?? 0);

export const linearDimensionsSorter = (a: PartnerPackagingV2, b: PartnerPackagingV2): number =>
  totalLinearDimension(a) - totalLinearDimension(b);

export const maxContentWeightSorter = (a: PartnerPackagingV2, b: PartnerPackagingV2): number =>
  (a.maxContentWeight.weight ?? 0) - (b.maxContentWeight.weight ?? 0);

/**
 * Groups active rows before inactive ones. The previous inline version tested
 * `a` (the row object, always truthy) instead of `a.active`, producing an
 * asymmetric comparator and an unstable, "random"-looking order (CUS-16855).
 */
export const activeSorter = (a: PartnerPackagingV2, b: PartnerPackagingV2): number =>
  a.active === b.active ? 0 : a.active ? -1 : 1;
